import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from 'axios';
import './LicenseAdminDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const LicenseAdminDashboard = ({ user }) => {
  const { t } = useTranslation();
  const [licenses, setLicenses] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    plan: '',
    status: '',
    type: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newLicense, setNewLicense] = useState({
    userEmail: '',
    plan: 'starter',
    type: 'monthly',
    duration: 30,
    customLimits: false,
    limits: {}
  });

  useEffect(() => {
    loadLicenseData();
  }, [currentPage, filters]);

  const loadLicenseData = async () => {
    try {
      setLoading(true);

      // Charger les statistiques des licences
      const statsResponse = await axios.get(`${API_BASE_URL}/api/license/admin/stats`, {
        withCredentials: true
      });
      setStats(statsResponse.data);

      // Charger la liste des licences avec pagination
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        ...filters
      });

      const licensesResponse = await axios.get(`${API_BASE_URL}/api/license/admin/licenses?${params}`, {
        withCredentials: true
      });

      setLicenses(licensesResponse.data.licenses || []);
      setTotalPages(Math.ceil((licensesResponse.data.total || 0) / 20));

    } catch (error) {
      console.error(t('licenseAdminDashboard.errors.loadData'), error);
      if (error.response?.status === 401) {
        toast.error(t('licenseAdminDashboard.errors.sessionExpired'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLicense = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/api/license/admin/create-license`, newLicense, {
        withCredentials: true
      });
      
      toast.success(t('licenseAdminDashboard.success.licenseCreated'));
      setShowCreateModal(false);
      setNewLicense({
        userEmail: '',
        plan: 'starter',
        type: 'monthly',
        duration: 30,
        customLimits: false,
        limits: {}
      });
      loadLicenseData();
    } catch (error) {
      console.error(t('licenseAdminDashboard.errors.createLicense'), error);
      toast.error(t('licenseAdminDashboard.errors.createLicenseMessage'));
    }
  };

  const handleUpdateLicense = async (licenseId, updates) => {
    try {
      await axios.put(`${API_BASE_URL}/api/license/admin/licenses/${licenseId}`, updates, {
        withCredentials: true
      });
      
      toast.success(t('licenseAdminDashboard.success.licenseUpdated'));
      loadLicenseData();
    } catch (error) {
      console.error(t('licenseAdminDashboard.errors.updateLicense'), error);
      toast.error(t('licenseAdminDashboard.errors.updateLicenseMessage'));
    }
  };

  const handleDeleteLicense = async (licenseId) => {
    if (window.confirm(t('licenseAdminDashboard.confirmations.deleteLicense'))) {
      try {
        await axios.delete(`${API_BASE_URL}/api/license/admin/licenses/${licenseId}`, {
          withCredentials: true
        });
        
        toast.success(t('licenseAdminDashboard.success.licenseDeleted'));
        loadLicenseData();
      } catch (error) {
        console.error(t('licenseAdminDashboard.errors.deleteLicense'), error);
        toast.error(t('licenseAdminDashboard.errors.deleteLicenseMessage'));
      }
    }
  };

  const cleanupExpiredLicenses = async () => {
    if (window.confirm('🧹 Nettoyer toutes les licences expirées ?')) {
      try {
        const response = await axios.post(`${API_BASE_URL}/api/license/admin/cleanup-expired`, {}, {
          withCredentials: true
        });
        
        toast.success(`✅ ${response.data.deletedCount} licences expirées supprimées !`);
        loadLicenseData();
      } catch (error) {
        console.error('❌ Erreur nettoyage:', error);
        toast.error('❌ Erreur lors du nettoyage');
      }
    }
  };

  const getStatusBadge = (license) => {
    const isExpired = new Date(license.endDate) < new Date();
    const daysRemaining = Math.ceil((new Date(license.endDate) - new Date()) / (1000 * 60 * 60 * 24));
    
    if (isExpired) {
      return <span className="status-badge expired">Expirée</span>;
    } else if (license.status === 'suspended') {
      return <span className="status-badge suspended">Suspendue</span>;
    } else if (license.status === 'cancelled') {
      return <span className="status-badge cancelled">Annulée</span>;
    } else if (daysRemaining <= 7) {
      return <span className="status-badge warning">Expire bientôt</span>;
    } else {
      return <span className="status-badge active">Active</span>;
    }
  };

  const getPlanBadge = (plan) => {
    const planConfig = {
      free: { label: 'Free Trial', color: 'gray' },
      starter: { label: 'Starter', color: 'blue' },
      professional: { label: 'Professional', color: 'purple' },
      enterprise: { label: 'Enterprise', color: 'gold' }
    };
    
    const config = planConfig[plan] || { label: plan, color: 'gray' };
    return <span className={`plan-badge ${config.color}`}>{config.label}</span>;
  };

  if (loading) {
    return (
      <div className="license-admin-loading">
        <div className="loading-spinner"></div>
        <p>Chargement des licences...</p>
      </div>
    );
  }

  return (
    <div className="license-admin-dashboard">
      {/* Header avec statistiques */}
      <div className="license-stats-header">
        <h2>🎫 Gestion des Licences</h2>
        
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <h3>{stats.totalLicenses || 0}</h3>
              <p>Licences totales</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>{stats.activeLicenses || 0}</h3>
              <p>Licences actives</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">🆓</div>
            <div className="stat-content">
              <h3>{stats.trialLicenses || 0}</h3>
              <p>Trials actifs</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <h3>{stats.monthlyRevenue || 0}€</h3>
              <p>Revenus mensuels</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="license-actions">
        <button 
          className="btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          ➕ Créer une licence
        </button>
        
        <button 
          className="btn-secondary"
          onClick={cleanupExpiredLicenses}
        >
          🧹 Nettoyer les expirées
        </button>
        
        <button 
          className="btn-outline"
          onClick={loadLicenseData}
        >
          🔄 Actualiser
        </button>
      </div>

      {/* Filtres */}
      <div className="license-filters">
        <select 
          value={filters.plan} 
          onChange={(e) => setFilters({...filters, plan: e.target.value})}
        >
          <option value="">Tous les plans</option>
          <option value="free">Free Trial</option>
          <option value="starter">Starter</option>
          <option value="professional">Professional</option>
          <option value="enterprise">Enterprise</option>
        </select>
        
        <select 
          value={filters.status} 
          onChange={(e) => setFilters({...filters, status: e.target.value})}
        >
          <option value="">Tous les statuts</option>
          <option value="active">Active</option>
          <option value="expired">Expirée</option>
          <option value="suspended">Suspendue</option>
          <option value="cancelled">Annulée</option>
        </select>
        
        <select 
          value={filters.type} 
          onChange={(e) => setFilters({...filters, type: e.target.value})}
        >
          <option value="">Tous les types</option>
          <option value="free_trial">Trial</option>
          <option value="monthly">Mensuel</option>
          <option value="annual">Annuel</option>
        </select>
      </div>

      {/* Table des licences */}
      <div className="licenses-table">
        <table>
          <thead>
            <tr>
              <th>Utilisateur</th>
              <th>Plan</th>
              <th>Type</th>
              <th>Statut</th>
              <th>Utilisation</th>
              <th>Expire le</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {licenses.map(license => (
              <tr key={license._id}>
                <td>
                  <div className="user-info">
                    <div>{license.user?.firstName} {license.user?.lastName}</div>
                    <small>{license.user?.email}</small>
                  </div>
                </td>
                <td>{getPlanBadge(license.plan)}</td>
                <td>
                  <span className={`type-badge ${license.type}`}>
                    {license.type === 'free_trial' ? 'Trial' : 
                     license.type === 'monthly' ? 'Mensuel' : 'Annuel'}
                  </span>
                </td>
                <td>{getStatusBadge(license)}</td>
                <td>
                  <div className="usage-info">
                    <div>Nœuds: {license.usage?.nodes || 0}/{license.limits?.maxNodes || 0}</div>
                    <div>API: {license.usage?.apiCalls || 0}/{license.limits?.maxApiCalls || 0}</div>
                  </div>
                </td>
                <td>
                  <div className="date-info">
                    {new Date(license.endDate).toLocaleDateString()}
                    <small>
                      {Math.ceil((new Date(license.endDate) - new Date()) / (1000 * 60 * 60 * 24))} jours
                    </small>
                  </div>
                </td>
                <td>
                  <div className="table-actions">
                    <button 
                      className="btn-icon"
                      onClick={() => handleUpdateLicense(license._id, { status: license.status === 'suspended' ? 'active' : 'suspended' })}
                      title={license.status === 'suspended' ? t('licenseAdminDashboard.reactivate') : t('licenseAdminDashboard.suspend')}
                    >
                      {license.status === 'suspended' ? '▶️' : '⏸️'}
                    </button>
                    <button 
                      className="btn-icon"
                      onClick={() => {
                        const newEndDate = prompt('Nouvelle date d\'expiration (YYYY-MM-DD):');
                        if (newEndDate) {
                          handleUpdateLicense(license._id, { endDate: new Date(newEndDate) });
                        }
                      }}
                      title={t('titles.editExpiration')}
                    >
                      📅
                    </button>
                    <button 
                      className="btn-icon danger"
                      onClick={() => handleDeleteLicense(license._id)}
                      title={t('titles.delete')}
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button 
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          ← Précédent
        </button>
        
        <span>Page {currentPage} sur {totalPages}</span>
        
        <button 
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Suivant →
        </button>
      </div>

      {/* Modal de création */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>➕ Créer une nouvelle licence</h3>
            
            <form onSubmit={handleCreateLicense}>
              <div className="form-group">
                <label>Email utilisateur :</label>
                <input 
                  type="email"
                  value={newLicense.userEmail}
                  onChange={(e) => setNewLicense({...newLicense, userEmail: e.target.value})}
                  placeholder={t('placeholders.email')}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Plan :</label>
                <select 
                  value={newLicense.plan}
                  onChange={(e) => setNewLicense({...newLicense, plan: e.target.value})}
                >
                  <option value="starter">Starter</option>
                  <option value="professional">Professional</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Type :</label>
                <select 
                  value={newLicense.type}
                  onChange={(e) => setNewLicense({...newLicense, type: e.target.value})}
                >
                  <option value="monthly">Mensuel</option>
                  <option value="annual">Annuel</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Durée (jours) :</label>
                <input 
                  type="number"
                  value={newLicense.duration}
                  onChange={(e) => setNewLicense({...newLicense, duration: parseInt(e.target.value)})}
                  placeholder={t('placeholders.daysNumber')}
                  min="1"
                  max="3650"
                />
                <small>Durée de validité de la licence en jours</small>
              </div>
              
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreateModal(false)}>
                  Annuler
                </button>
                <button type="submit">
                  Créer la licence
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LicenseAdminDashboard;
